import _ from 'lodash'

import api from '@/api'

const namespaced = true

const state = {
  planNextTripDrafts: [],
  incomingFleets: [],
  tugsDropdown: [],
  bargesDropdown: [],
  blendingSchema: []
}

const getters = {
  planNextTripDrafts: state => state.planNextTripDrafts,
  incomingFleets: state => state.incomingFleets,
  tugsDropdown: state => state.tugsDropdown,
  bargesDropdown: state => state.bargesDropdown,
  blendingSchema: state => state.blendingSchema
}

const mutations = {
  SET_PLAN_NEXT_TRIP_DRAFTS: (state, payload) => {
    state.planNextTripDrafts = payload
  },
  SET_INCOMING_FLEETS: (state, payload) => {
    state.incomingFleets = payload
  },
  SET_TUGS_DROPDOWN: (state, payload) => {
    state.tugsDropdown = payload
  },
  SET_BARGES_DROPDOWN: (state, payload) => {
    state.bargesDropdown = payload
  },
  SET_BLENDING_SCHEMA: (state, payload) => {
    state.blendingSchema = payload
  }
}

const actions = {
  async fetchPlanNextTripDrafts({ commit, rootGetters }, payload) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']
      const filter = {
        search: filterConfig.search,
        page: filterConfig.currentPage || 1,
        limit: filterConfig.pageSize || 10,
        ...payload
      }
      const { data } = await api.getPlanNextTripDrafts(filter)
      if (data.error) {
        commit('SET_PLAN_NEXT_TRIP_DRAFTS', [])
        commit(`Table/SET_TOTAL_DATA=`, 0, { root: true })
        commit(`Table/SET_TOTAL_PAGE=`, 1, { root: true })
        return
      }
      if (!data.data) {
        commit('SET_PLAN_NEXT_TRIP_DRAFTS', data)
        return
      }
      commit('SET_PLAN_NEXT_TRIP_DRAFTS', data.data)
      commit(`Table/SET_TOTAL_DATA`, data.total, { root: true })
      commit(`Table/SET_TOTAL_PAGE`, data.pages, { root: true })
    } catch (err) {
      throw err
    }
  },
  async fetchIncomingFleets({ commit, rootGetters }, payload) {
    // isSecondaryTable is for updating secondary table config, so its not use default table config
    const isSecondaryTable = payload?.isSecondaryTable ?? false
    if(isSecondaryTable) delete payload.isSecondaryTable
    try {
      const filterConfig = isSecondaryTable ? rootGetters['Table/secondaryTableDefaultConfig'] : rootGetters['Table/defaultConfig']
      const filter = {
        search: filterConfig.search,
        page: filterConfig.currentPage || 1,
        limit: filterConfig.pageSize || 10,
        ...(payload.filter && payload.filter.sort_dir && payload.filter.sort_by ? {
          sort_by: payload.filter.sort_by,
          sort_dir: payload.filter.sort_dir
        } : {})
      }
      const { data } = await api.getIncomingFleets(filter)
      if (data.error) {
        commit('SET_INCOMING_FLEETS', [])
        commit(`Table/SET_TOTAL_DATA${isSecondaryTable ? '_SECONDARY_TABLE' : ''}`, 0, { root: true })
        commit(`Table/SET_TOTAL_PAGE${isSecondaryTable ? '_SECONDARY_TABLE' : ''}`, 1, { root: true })
        return
      }
      if (!data.data) {
        commit('SET_INCOMING_FLEETS', data)
        return
      }
      commit('SET_INCOMING_FLEETS', data.data)
      commit(`Table/SET_TOTAL_DATA${isSecondaryTable ? '_SECONDARY_TABLE' : ''}`, data.total, { root: true })
      commit(`Table/SET_TOTAL_PAGE${isSecondaryTable ? '_SECONDARY_TABLE' : ''}`, data.pages, { root: true })
    } catch (err) {
      throw err
    }
  },
  async fetchTugs({ commit }, payload) {
    try {
      const filter = {
        ...payload,
        is_active: true,
        is_exclude_image: true,
        is_exclude_tug_barge_combinations: true
      }
      const {data} = await api.getTugManagements(filter)
      if (data) {
        commit('SET_TUGS_DROPDOWN', data);
      }
      return;
    } catch (error) {
      commit('SET_TUGS_DROPDOWN', []);
      throw error.data || error;
    }
  },
  async fetchBarges({ commit }, payload) {
    try {
      const filter = {
        ...payload,
        is_exclude_image: true,
        is_exclude_tug_barge_combinations: true
      }
      const {data} = await api.getBargeManagements(filter)
      if (data) {
        const filteredActiveData = data.filter(item => item.active === true);
        commit('SET_BARGES_DROPDOWN', filteredActiveData);
      }
      return;
    } catch (error) {
      commit('SET_BARGES_DROPDOWN', []);
      throw error.data || error;
    }
  },
  async checkDockingSchedule(_, payload) {
    try {
      const { data } = await api.getDockingScheduleByCombination(payload)
      return data
    } catch (error) {
      throw error.data || error
    }
  },
  async postPlanNextTrip(_, payload) {
    const { id, form } = payload;
    try {
      await api.postPlanNextTripDraft(id, form);
    } catch (error) {
      throw error.data;
    }
  },
  async postManyPlanNextTrip({ dispatch }, payload) {
    try {
      const { items } = payload;
      if (items && items.length > 0) {
        // Iterate over each item and post it individually
        for (const item of items) {
          const body = {
            id: item.vessel_line_up_id,
            form: {
              ..._.pick(item,['vessel_name','lay','can','pol','pod','total_tonnage','loading_date']),
              product_details: item.product_details.map(product => ({
                ..._.pick(product,['name','is_blending','order_level','total_tonnage']),
                tug_and_barge_combinations: product.tug_and_barge_combinations.map(combination => ({
                  ..._.pick(combination,[
                    'tug_id',
                    'barge_id',
                    'fleet_id',
                    'owner_id',
                    'position',
                    'tug_name',
                    'tonnage',
                    'barge_name',
                    'owner_name', 
                    'fleet_id',
                    'order_level',
                    'estimated_trip_start',
                    'blending_schema'])
                }))
              }))
            }
          }
          await dispatch('postPlanNextTrip', body);
        }
      }
    } catch (error) {
      throw error.data || error;
    }
  },
  async fetchBlendingSchema({commit}) {
    try {
      const { data } = await api.getPlanNextTripBlendingSchema()
      if (data) {
        commit('SET_BLENDING_SCHEMA', data);
      }
      return;
    } catch (error) {
      commit('SET_BLENDING_SCHEMA', []);
      throw error.data || error;
    }
  },
  async postGenerateToBLU(_, payload) {
    const { id, form } = payload;
    try {
      const { data } = await api.postPlanNextTripGenerateToBLU(id, form);
      if(data) {
        return data.success;
      }
      return false;
    } catch (error) {
      throw error.data;
    }
  },
  async postManyPlanNextTripGenerateToBLU({ dispatch }, payload) {
    try {
      const { items } = payload;
      let successCount = 0;
      let failedCount = 0;
      if (items && items.length > 0) {
        // Iterate over each item and post it individually
        for (const item of items) {
          const body = {
            id: item.vessel_line_up_id,
            form: {
              ..._.pick(item,['vessel_name','lay','can','pol','pod','total_tonnage', 'assigned_tonnage', 'loading_date']),
              product_details: item.product_details.map(product => ({
                ..._.pick(product,['name','is_blending','order_level','total_tonnage','assigned_tonnage']),
                tug_and_barge_combinations: product.tug_and_barge_combinations.map(combination => ({
                  ..._.pick(combination,[
                    'tug_id',
                    'barge_id',
                    'max_load',
                    'min_load',
                    'fleet_id',
                    'owner_id',
                    'position',
                    'tug_name',
                    'tonnage',
                    'barge_name',
                    'owner_name', 
                    'fleet_id',
                    'order_level',
                    'estimated_trip_start',
                    'blending_schema'])
                }))
              }))
            }
          }
          try {
            const success = await dispatch('postGenerateToBLU', body);
            if(success) {
              successCount++;
            } else {
              failedCount++;
            }
          } catch(_) {
            failedCount++
          }
        }
      }
      return { successCount, failedCount };
    } catch (error) {
      throw error.data || error;
    }
  }
}

export default { namespaced, state, getters, mutations, actions }